import { useLayoutEffect, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiPlayCircle } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { FAQItem, faqs } from './HomeMockups';
import HeroAnimation from '../components/HeroAnimation/HeroAnimation';
import HomeNav from './HomeNav';
import './Home.css';

/* Student-management dashboard mockup (hand-built CSS, no images) */
const students = [
  { initials: 'MR', name: 'Mateo Ramírez', level: 'B1', status: 'Active',          statusMod: 'active',    avatar: '#116D6E', progress: 8,  meta: ['Week 1 of 52',  '2 lessons / wk'] },
  { initials: 'HP', name: 'Hyejin Park',   level: 'C1', status: 'Active',          statusMod: 'active',    avatar: '#27374D', progress: 84, meta: ['Week 43 of 52', '4 lessons / wk'] },
  { initials: 'AD', name: 'Aylin Demir',   level: 'A2', status: 'Needs attention', statusMod: 'attention', avatar: '#526D82', progress: 28, meta: ['Week 14 of 52', '2 lessons / wk'] },
];

const StudentDashboardMockup = () => (
  <div className="hp-sm-card">
    <div className="hp-sm-chrome">
      <div className="hp-sm-dots">
        <span className="hp-sm-dot hp-sm-dot--r" />
        <span className="hp-sm-dot hp-sm-dot--y" />
        <span className="hp-sm-dot hp-sm-dot--g" />
      </div>
      <div className="hp-sm-url">app.teachesl.pro/students</div>
    </div>
    <div className="hp-sm-body">
      <div className="hp-sm-filters">
        <span className="hp-sm-filter hp-sm-filter--active">Active · 12</span>
        <span className="hp-sm-filter hp-sm-filter--muted">Completed · 3</span>
        <span className="hp-sm-filter hp-sm-filter--muted">Draft · 1</span>
      </div>
      <div className="hp-sm-rows">
        {students.map(s => (
          <div key={s.initials} className="hp-sm-row">
            <div className="hp-sm-row-top">
              <span className="hp-sm-avatar" style={{ background: s.avatar }}>{s.initials}</span>
              <span className="hp-sm-name">{s.name}</span>
              <span className="hp-sm-cefr">{s.level}</span>
              <span className={`hp-sm-status hp-sm-status--${s.statusMod}`}>{s.status}</span>
            </div>
            <div className="hp-sm-bar"><div className="hp-sm-bar-fill" style={{ width: `${s.progress}%` }} /></div>
            <div className="hp-sm-meta">
              {s.meta.map(m => <span key={m}>{m}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* Earnings calculator: turns planning time saved into hours and income.
   An estimate only - assumes the time saved is filled with paid lessons. */
const CURRENCIES = { GBP: '£', USD: '$', EUR: '€' };
const WEEKS_PER_MONTH = 52 / 12;
const PREMIUM_PRICE_GBP = 14.99;

const EarningsCalculator = () => {
  const [currency, setCurrency]   = useState('GBP');
  const [studentCount, setStudentCount] = useState(10);
  const [planMinutes, setPlanMinutes]   = useState(30);
  const [rate, setRate]           = useState(25);
  const [savedPct, setSavedPct]   = useState(50);

  const symbol = CURRENCIES[currency];
  const hoursBack = (studentCount * planMinutes * (savedPct / 100) / 60) * WEEKS_PER_MONTH;
  const extraIncome = hoursBack * (Number(rate) || 0);
  const costMultiple = extraIncome / PREMIUM_PRICE_GBP;

  const fmtMoney = n => `${symbol}${Math.round(n).toLocaleString()}`;

  return (
    <div className="hp-calc">
      <div className="hp-calc-head">
        <h3>What could your time be worth?</h3>
        <select
          className="hp-calc-currency"
          value={currency}
          onChange={e => setCurrency(e.target.value)}
          aria-label="Currency"
        >
          {Object.entries(CURRENCIES).map(([code, sym]) => (
            <option key={code} value={code}>{sym} {code}</option>
          ))}
        </select>
      </div>

      <div className="hp-calc-fields">
        <label className="hp-calc-field">
          <span className="hp-calc-label">Students you teach <strong>{studentCount}</strong></span>
          <input type="range" min="1" max="40" value={studentCount}
            onChange={e => setStudentCount(Number(e.target.value))} />
        </label>
        <label className="hp-calc-field">
          <span className="hp-calc-label">Planning time per student, per week <strong>{planMinutes} min</strong></span>
          <input type="range" min="10" max="90" step="5" value={planMinutes}
            onChange={e => setPlanMinutes(Number(e.target.value))} />
        </label>
        <label className="hp-calc-field">
          <span className="hp-calc-label">Planning time TeachESLPro saves you <strong>{savedPct}%</strong></span>
          <input type="range" min="20" max="80" step="5" value={savedPct}
            onChange={e => setSavedPct(Number(e.target.value))} />
        </label>
        <label className="hp-calc-field">
          <span className="hp-calc-label">Your hourly lesson rate</span>
          <span className="hp-calc-rate">
            <span>{symbol}</span>
            <input type="number" min="0" inputMode="decimal" value={rate}
              onChange={e => setRate(e.target.value)} />
          </span>
        </label>
      </div>

      <div className="hp-calc-results">
        <div className="hp-calc-stat">
          <span className="hp-calc-stat-value">{hoursBack.toFixed(1)}</span>
          <span className="hp-calc-stat-label">hours back each month</span>
        </div>
        <div className="hp-calc-stat hp-calc-stat--highlight">
          <span className="hp-calc-stat-value">{fmtMoney(extraIncome)}</span>
          <span className="hp-calc-stat-label">potential extra income a month</span>
        </div>
      </div>

      <p className="hp-calc-note">
        {currency === 'GBP' && costMultiple >= 1
          ? `That's ${Math.floor(costMultiple)}× the cost of Premium (£${PREMIUM_PRICE_GBP}/month). `
          : `Premium costs £${PREMIUM_PRICE_GBP}/month. `}
        An estimate, not a guarantee: it assumes you fill the time you save with paid lessons.
      </p>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────────────────────────────────────────── */
const Home = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const { hash } = useLocation();

  /* Redirect logged-in users to the logged-in homepage */
  useEffect(() => {
    if (!loading && currentUser) {
      navigate('/home', { replace: true });
    }
  }, [currentUser, loading, navigate]);

  /* Hide existing Layout header/footer synchronously before first paint */
  useLayoutEffect(() => {
    document.body.classList.add('homepage-active');
    return () => document.body.classList.remove('homepage-active');
  }, []);

  /* Fade-up scroll animations + active nav section tracking */
  useEffect(() => {
    const fadeObs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('hp-visible'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.hp-fade-up').forEach(el => fadeObs.observe(el));

    const secObs = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { threshold: 0.35 }
    );
    document.querySelectorAll('section[id]').forEach(el => secObs.observe(el));

    return () => { fadeObs.disconnect(); secObs.disconnect(); };
  }, []);

  /* Links from other pages (e.g. the nav on /demo) arrive as /#section */
  useEffect(() => {
    const el = hash && document.getElementById(hash.slice(1));
    if (!el) return;
    const t = setTimeout(() => window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72 }), 50);
    return () => clearTimeout(t);
  }, [hash]);

  const smoothScroll = (id, e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="hp-page hp-home">
      <Helmet>
        <title>TeachESLPro - Plan less, teach more, grow your ESL tutoring business</title>
        <meta name="description" content="Placement tests, personalised lesson pathways and bespoke, hand-made resources, so freelance ESL tutors spend less time planning and more time on paid lessons." />
      </Helmet>

      <HomeNav activeSection={activeSection} />

      {/* ── SECTION 1: HERO ────────────────────────────────────────────── */}
      <section id="hero" className="hp-hero">
        <div className="hp-hero-inner">

          {/* Left: copy */}
          <div className="hp-hero-copy hp-fade-up">
            <div className="hp-hero-badge">For private &amp; freelance ESL tutors</div>
            <h1>
              Plan less. Teach more.{' '}
              <em className="hp-teal">Grow your tutoring business.</em>
            </h1>
            <p className="hp-hero-sub">
              A placement test and a personalised pathway for every student in minutes, plus hand-made resources and bespoke lessons made to order. Put the hours you used to spend planning into paid lessons, new students and a more professional service.
            </p>
            <div className="hp-hero-ctas">
              <Link to="/signup" className="hp-btn hp-btn-accent hp-btn-lg">Start your free trial</Link>
              <Link to="/book-a-demo" className="hp-btn hp-btn-white-outline hp-btn-lg">
                Book a free demo
              </Link>
            </div>
            <Link to="/demo" className="hp-hero-demo-link">
              <FiPlayCircle aria-hidden="true" /> Watch the 4-minute demo
            </Link>
            <div className="hp-hero-trust">
              <div className="hp-avatars">
                <span className="hp-avatar">S</span>
                <span className="hp-avatar">M</span>
                <span className="hp-avatar">L</span>
                <span className="hp-avatar">R</span>
              </div>
              Trusted by ESL teachers worldwide
            </div>
          </div>

          {/* Right: animated walkthrough (existing component, unchanged) */}
          <div className="hp-hero-visual hp-fade-up hp-delay-2">
            <HeroAnimation />
          </div>

        </div>
      </section>

      {/* ── SECTION 2: PROOF BAR ──────────────────────────────────────── */}
      <div className="hp-proof-bar">
        <div className="hp-proof-bar-inner">
          {[
            'Send student a placement test',
            'Pathways generated in minutes',
            'Bespoke resources in 72 hours',
            '7-day free trial',
          ].map((item, i, arr) => (
            <span key={item} style={{ display: 'contents' }}>
              <span className="hp-proof-item">{item}</span>
              {i < arr.length - 1 && <span className="hp-proof-dot" />}
            </span>
          ))}
        </div>
      </div>

      {/* ── SECTION 3: HOW IT WORKS ───────────────────────────────────── */}
      <section id="how-it-works" className="hp-section hp-hiw">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">How it works</span>
            <h2>A structured pathway for <em className="hp-teal">every student</em></h2>
            <p>Five steps from a blank slate to a fully personalised lesson plan.</p>
          </div>

          <div className="hp-hiw-steps">
            {[
              { icon: '📩', title: 'Send placement test',   desc: 'Share a link with your student. They complete reading, writing & listening - independently, in their own time.' },
              { icon: '📊', title: 'Review skill levels',   desc: 'Receive a clear level breakdown for each skill. Add your own assessment of speaking and comprehension.' },
              { icon: '🎯', title: 'Set goals & schedule',  desc: "Add the student's goals, interests, lessons per week and target duration. More context = better pathway." },
              { icon: '⚡', title: 'Pathway generated',     desc: 'A full lesson pathway is built automatically, with overviews for every lesson and matching library resources linked in where available.' },
              { icon: '✏️', title: 'Teach & adapt',         desc: 'Edit lessons, request bespoke resources and track progress as your student advances through the pathway.' },
            ].map((step, i) => (
              <div key={step.title} className={`hp-hiw-step hp-fade-up hp-delay-${i + 1}`}>
                <div className="hp-hiw-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3b: GROW YOUR BUSINESS ────────────────────────────── */}
      <section id="grow" className="hp-section hp-showcase hp-showcase-alt">
        <div className="hp-showcase-inner hp-container">
          <div className="hp-showcase-copy hp-fade-up">
            <span className="hp-section-tag">Grow your business</span>
            <h2>You&apos;re not just a teacher. <em className="hp-teal">You run a business.</em></h2>
            <p>Every hour spent planning from scratch is an hour you can&apos;t teach or charge for. TeachESLPro does the groundwork, so your time goes into the parts of the job that earn.</p>
            <div className="hp-bullets">
              {[
                { icon: '📈', title: 'Take on more students',         desc: 'Set up a new student in minutes, not evenings. Use the time you get back for more paid lessons.' },
                { icon: '🤝', title: 'Win more trial lessons',        desc: 'Start every new student with a proper placement test and a clear plan. That first impression helps turn trial lessons into regular bookings.' },
                { icon: '🔁', title: 'Keep students for longer',      desc: 'Share the pathway with students or parents so they can see the plan and their progress, and know why they keep booking.' },
                { icon: '💼', title: 'Offer higher-value lessons',    desc: 'Bespoke resources let you take on exam prep, business English and other specialist work without days of preparation.' },
              ].map(b => (
                <div key={b.title} className="hp-bullet">
                  <div className="hp-bullet-icon">{b.icon}</div>
                  <div className="hp-bullet-text">
                    <strong>{b.title}</strong>
                    <span>{b.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hp-showcase-visual hp-fade-up hp-delay-2">
            <EarningsCalculator />
          </div>
        </div>
      </section>

      {/* ── SECTION 4: STUDENT MANAGEMENT SHOWCASE ────────────────────── */}
      <section id="student-management" className="hp-section hp-showcase">
        <div className="hp-showcase-inner hp-container">
          <div className="hp-showcase-copy hp-fade-up">
            <span className="hp-section-tag">Student management</span>
            <h2>All your students, <em className="hp-teal">one place</em></h2>
            <p>See every student's pathway, level, progress and lesson schedule at a glance. No spreadsheets. No guesswork.</p>
            <div className="hp-bullets">
              {[
                { icon: '👁️', title: 'Instant progress overview', desc: 'See how far each student has progressed - lessons completed, percentage done, lessons per week.' },
                { icon: '🔍', title: 'Filter by status',           desc: 'Quickly filter active, completed and draft pathways. Search by student name or level.' },
                { icon: '➕', title: 'Add a pathway in seconds',    desc: 'Start a new student pathway directly from the dashboard. The system does the heavy lifting.' },
              ].map(b => (
                <div key={b.title} className="hp-bullet">
                  <div className="hp-bullet-icon">{b.icon}</div>
                  <div className="hp-bullet-text">
                    <strong>{b.title}</strong>
                    <span>{b.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hp-showcase-visual hp-fade-up hp-delay-2">
            <StudentDashboardMockup />
          </div>
        </div>
      </section>

      {/* ── SECTION 5: FEATURES GRID ──────────────────────────────────── */}
      <section id="features" className="hp-section hp-features">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">Full feature set</span>
            <h2>Everything a private tutor <em className="hp-teal">actually needs</em></h2>
          </div>

          <div className="hp-features-grid">
            {/* Card 1: Placement Test */}
            <div className="hp-feature-card hp-fade-up">
              <div className="hp-feature-icon">📋</div>
              <h3>Multi-skill Placement Test</h3>
              <p>Send students a single link. They complete a structured test covering reading, writing and listening. You receive a clear level breakdown for each skill - then add your own speaking and comprehension assessment. A professional start that helps turn trial lessons into regular students.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">All plans</span>
            </div>

            {/* Card 2: Bespoke */}
            <div className="hp-feature-card hp-fade-up hp-delay-1">
              <div className="hp-feature-icon">✍️</div>
              <h3>Bespoke Lesson Requests</h3>
              <p>Need something specific? Request a custom resource directly from a lesson overview in your student's pathway or from your dashboard. TeachESLPro delivers a professionally designed resource tailored to your request within 72 hours. Ideal for specialist, higher-value lessons like exam prep or business English. Every bespoke resource joins the library 7 days after delivery, so the catalogue grows with real teaching needs.</p>
              <span className="hp-feature-badge hp-feature-badge--accent">Premium Bespoke plan</span>
            </div>

            {/* Card 3: Full width, navy - Pathways */}
            <div className="hp-feature-card hp-feature-card--full hp-fade-up hp-delay-2">
              <div className="hp-feature-inner-grid">
                <div>
                  <div className="hp-feature-icon">⚡</div>
                  <h3>Automated Learning Pathways</h3>
                  <p>Based on placement results, teacher input, student goals, interests, lesson frequency and target duration, a full staged lesson pathway is generated automatically. Each lesson includes an overview and, where a matching catalogue resource exists, it is linked automatically. You can choose to allow students or parents to view the learning pathway, so they can see the plan and the progress they&apos;re paying for.</p>
                  <span className="hp-feature-badge hp-feature-badge--light">Unique to TeachESLPro</span>
                </div>
                <div className="hp-feature-checklist">
                  {[
                    'Reading, writing & listening from placement',
                    'Teacher-assessed speaking level',
                    'Student goals & target level',
                    'Lessons per week & timeframe',
                    'Interests & topic preferences',
                    'Matching library resources linked in',
                  ].map(item => (
                    <div key={item} className="hp-feature-check">{item}</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card 4: Hand-made library */}
            <div className="hp-feature-card hp-fade-up hp-delay-3">
              <div className="hp-feature-icon">🗂️</div>
              <h3>A hand-made library that grows with you</h3>
              <p>Every resource is hand-made by professional teachers. The library is growing all the time: every bespoke request joins it 7 days after delivery, so it is shaped by what tutors actually teach.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">All plans</span>
            </div>

            {/* Card 5: Organise */}
            <div className="hp-feature-card hp-fade-up hp-delay-4">
              <div className="hp-feature-icon">📁</div>
              <h3>Organise &amp; Manage Resources</h3>
              <p>Save, organise and manage your downloaded resources. Link collections directly to a student's pathway so everything is in one place.</p>
              <span className="hp-feature-badge hp-feature-badge--teal">Premium &amp; above</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: TESTIMONIALS ───────────────────────────────────── */}
      <section id="testimonials" className="hp-section hp-testimonials">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">What teachers say</span>
            <h2>Real feedback from <em className="hp-teal">real teachers</em></h2>
          </div>

          <div className="hp-testimonials-grid">
            <div className="hp-testimonial hp-testimonial--featured hp-fade-up">
              <div className="hp-testimonial-stars">★★★★★</div>
              <p className="hp-testimonial-text">
                "I used to spend hours every week planning from scratch for each student. The pathway system changed everything - I set it up once and have a structured plan ready to go. My students have noticed how much more organised the lessons feel."
              </p>
              <div className="hp-testimonial-author">Sarah J.</div>
              <div className="hp-testimonial-role">Freelance ESL tutor · 14 students</div>
            </div>

            <div className="hp-testimonial hp-fade-up hp-delay-1">
              <div className="hp-testimonial-stars">★★★★★</div>
              <p className="hp-testimonial-text">
                "The placement test alone is worth the subscription. I used to guess levels from a quick conversation. Now I have a proper breakdown before the first lesson even starts."
              </p>
              <div className="hp-testimonial-author">Marco A.</div>
              <div className="hp-testimonial-role">Private tutor · Online, Italy</div>
            </div>
          </div>

        </div>
      </section>

      {/* ── SECTION 7: PRICING ────────────────────────────────────────── */}
      <section id="pricing" className="hp-section hp-pricing">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">Pricing</span>
            <h2>Simple, transparent <em className="hp-teal">pricing</em></h2>
            <p>One monthly price, not a fee per student, so the more students you teach, the less it costs per student. All plans include the placement test and automated learning pathways. Cancel any time.</p>
          </div>

          <div className="hp-pricing-grid">
            {/* Standard */}
            <div className="hp-pricing-card-wrap hp-fade-up">
              <div className="hp-pricing-card">
                <div className="hp-pricing-name">Standard</div>
                <div className="hp-pricing-price">
                  <span className="hp-pricing-amount">£6.99</span>
                  <span className="hp-pricing-period">/month</span>
                </div>
                <div className="hp-pricing-per-student">£2.33 per student with 3 students</div>
                <p className="hp-pricing-desc">For teachers getting started with structured pathways.</p>
                <ul className="hp-pricing-features">
                  {[
                    'Student placement test',
                    '3 student pathways',
                    'Access to the growing resource library',
                    '8 downloads per month',
                  ].map(f => <li key={f} className="hp-pricing-feature">{f}</li>)}
                </ul>
                <Link to="/signup" className="hp-btn hp-btn-outline hp-pricing-cta">Get started</Link>
              </div>
            </div>

            {/* Premium */}
            <div className="hp-pricing-card-wrap hp-fade-up hp-delay-1">
              <div className="hp-pricing-popular">Most popular</div>
              <div className="hp-pricing-card hp-pricing-card--featured">
                <div className="hp-pricing-name">Premium</div>
                <div className="hp-pricing-price">
                  <span className="hp-pricing-amount">£14.99</span>
                  <span className="hp-pricing-period">/month</span>
                </div>
                <div className="hp-pricing-per-student">£1.50 per student with 10 students</div>
                <p className="hp-pricing-desc">For growing teachers managing a full roster of students.</p>
                <ul className="hp-pricing-features">
                  {[
                    'Everything in Standard',
                    'Unlimited student pathways',
                    'Organise & manage saved resources into student collections',
                    'Unlimited downloads',
                  ].map(f => <li key={f} className="hp-pricing-feature">{f}</li>)}
                </ul>
                <Link to="/signup" className="hp-btn hp-btn-primary hp-pricing-cta">Get started</Link>
              </div>
            </div>

            {/* Premium Bespoke */}
            <div className="hp-pricing-card-wrap hp-fade-up hp-delay-2">
              <div className="hp-pricing-card">
                <div className="hp-pricing-name">Premium Bespoke</div>
                <div className="hp-pricing-price">
                  <span className="hp-pricing-amount">£21.99</span>
                  <span className="hp-pricing-period">/month</span>
                </div>
                <div className="hp-pricing-per-student">£2.20 per student with 10 students</div>
                <p className="hp-pricing-desc">For teachers offering specialist lessons with fully tailored resources.</p>
                <ul className="hp-pricing-features">
                  {[
                    'Everything in Premium',
                    '2 bespoke resource requests per month',
                    'Custom resources delivered in 72 hours',
                    'Bespoke resources yours to keep and re-download anytime',
                  ].map(f => <li key={f} className="hp-pricing-feature">{f}</li>)}
                </ul>
                <Link to="/signup" className="hp-btn hp-btn-outline hp-pricing-cta">Get started</Link>
              </div>
            </div>
          </div>

          {/* Schools & academies */}
          <div className="hp-pricing-business hp-fade-up">
            <div className="hp-pricing-business-main">
              <span className="hp-section-tag">Schools &amp; academies</span>
              <div className="hp-pricing-name">Business</div>
              <div className="hp-pricing-price">
                <span className="hp-pricing-period">from</span>
                <span className="hp-pricing-amount">£80</span>
                <span className="hp-pricing-period">/month for up to 5 teachers</span>
              </div>
              <p className="hp-pricing-business-desc">
                For language schools, academies and course providers. One plan for your whole team: pay for teachers, not students.
                Up to 8 teachers £100/month, up to 20 teachers £150/month. More than 20? <Link to="/contact">Contact us</Link>.
                {' '}<Link to="/schools">Find out more about TeachESLPro for schools</Link>.
              </p>
            </div>
            <div className="hp-pricing-business-side">
              <ul className="hp-pricing-features">
                {[
                  'Pathways for 1-to-1 students and group classes',
                  'Unlimited pathways and downloads',
                  '2 bespoke resource requests per teacher each month',
                  'Invite teachers and see every class in one place',
                ].map(f => <li key={f} className="hp-pricing-feature">{f}</li>)}
              </ul>
              <Link to="/signup?plan=business" className="hp-btn hp-btn-primary hp-pricing-cta">Start a 7-day free trial</Link>
            </div>
          </div>

          <div className="hp-pricing-footer hp-fade-up">
            <small>The 7-day free trial is available on Standard and Business.</small>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: FAQ ────────────────────────────────────────────── */}
      <section id="faq" className="hp-section hp-faq">
        <div className="hp-container">
          <div className="hp-section-header hp-fade-up">
            <span className="hp-section-tag">FAQs</span>
          </div>

          <div className="hp-faq-list hp-fade-up">
            {faqs.map((item, i) => (
              <FAQItem
                key={i}
                q={item.q}
                a={item.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 9: FINAL CTA ──────────────────────────────────────── */}
      <section id="trial" className="hp-final-cta">
        <div className="hp-fade-up">
          <h2>
            Ready to grow your <em className="hp-cta-accent">tutoring business?</em>
          </h2>
          <p>Spend less time planning and put those hours into more students, happier students and a stronger income.</p>
          <div className="hp-final-cta-btns">
            <Link to="/signup" className="hp-btn hp-btn-accent hp-btn-lg">Start your free trial</Link>
            <Link to="/book-a-demo" className="hp-btn hp-btn-white-outline hp-btn-lg">Book a free demo</Link>
          </div>
          <p className="hp-final-cta-fine">7-day trial for Standard membership · Cancel any time</p>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-footer-grid">
          <div>
            <Link to="/" className="hp-footer-logo">Teach<span className="hp-esl">ESL</span>Pro</Link>
            <p className="hp-footer-tagline">Placement, pathways and hand-made resources for private &amp; freelance ESL tutors.</p>
          </div>
          <div className="hp-footer-col">
            <h4>Product</h4>
            <ul>
              <li><a href="#how-it-works" onClick={e => smoothScroll('how-it-works', e)}>How it works</a></li>
              <li><a href="#features" onClick={e => smoothScroll('features', e)}>Features</a></li>
              <li><a href="#pricing" onClick={e => smoothScroll('pricing', e)}>Pricing</a></li>
            </ul>
          </div>
          <div className="hp-footer-col">
            <h4>Company</h4>
            <ul>
              <li><Link to="/book-a-demo">Book a demo</Link></li>
              <li><Link to="/free-placement-test">Free placement test</Link></li>
              <li><Link to="/affiliates">Become an affiliate</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/privacy">Privacy policy</Link></li>
              <li><Link to="/terms">Terms of service</Link></li>
            </ul>
          </div>
        </div>
        <div className="hp-footer-bottom">
          <span>© {new Date().getFullYear()} TeachESLPro. All rights reserved.</span>
        </div>
      </footer>

    </div>
  );
};

export default Home;
